import React, {
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  useCallback,
  useMemo,
} from "react";
import moment from "moment";
import { PageHeading } from "../../components";
import NonRevenueWaterFilter from "../../components/non-revenue-water-filter/non-revenue-water-filter";
import {
  AreaChart,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Area,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import NonRevenueItem from "./non-revenue-item";
import getNonRevenueWaterData from "src/main_app/utils/customer_utils/get-non-revenue-water-data";
import {
  // dataLineChartNRW,
  dataPieChartNRW,
  COLORS,
} from "src/main_app/utils/constant-data";

const NonRevenueWaterPage = () => {
  const filterRef = useRef(null);
  const [avgResidentialMeter, setAvgResidentialMeter] = useState(0);
  const [avgResidentialMechanicalMeter, setAvgResidentialMechanicalMeter] = useState(0);
  const [avgBusinessMeter, setAvgBusinessMeter] = useState(0);
  const [avgBulkUsage, setAvgBulkUsage] = useState(0);
  const [totalBusinessMeter, setTotalBusinessMeter] = useState(0);
  const [totalResidentialMeter, setTotalResidentialMeter] = useState(0);
  const [totalResidentialMechanicalMeter, setTotalResidentialMechanicalMeter] = useState(0);
  const [dataPieChart, setDataPieChart] = useState(dataPieChartNRW);
  const [dataLineChartNRW, setDataLineChartNRW] = useState([]);
  const [nonRevenueWater, setNonRevenueWater] = useState(0);

  const xTicks = useMemo(() => {
    if (!dataLineChartNRW || !dataLineChartNRW.length) return [];
    const seen = new Set();
    const ticks = [];
    for (const d of dataLineChartNRW) {
      if (!d || !d.date_time) continue;
      const dayKey = moment.utc(d.date_time).startOf("day").format("YYYY-MM-DD");
      if (!seen.has(dayKey)) {
        seen.add(dayKey);
        ticks.push(d.date_time);
      }
    }
    return ticks;
  }, [dataLineChartNRW]);

  const rounded = useCallback((num) => {
    return Math.round(num * 1000) / 1000;
  }, []);

  const rounded1Decimal = useCallback((num) => {
    return Math.round(num * 10) / 10;
  }, []);

  // Color mapping for consistent colors across charts
  const variableColors = useMemo(() => {
    return {
      "Bulk usage": "#FF8042", // Orange
      "Residential digital meters": "#09A3B3", // Cyan/Teal
      "Residential mechanical meters": "#00C49F", // Green
      "Business meters": "#259bd3", // Blue
      "Other usage": "#FFBB28", // Yellow
      "Non revenue water": "#9d2a91", // Purple
      "Residential usage": "#09A3B3", // For line chart
    };
  }, []);

  const getColorForVariable = useCallback((name) => {
    return variableColors[name] || COLORS[0];
  }, [variableColors]);

  const loadData = async (filters) => {
    // Called when user clicks Apply
    if (filters?.startDate)
      filters.startDate = moment(filters.startDate).format("YYYY-MM-DD");
    if (filters?.endDate)
      filters.endDate = moment(filters.endDate).format("YYYY-MM-DD");
    console.log("Filters applied:", filters);
    // example: { startDate: ..., endDate: ..., zone: "CameronPark1WPS" }
    filters.zone = "CameronPark1WPS";
    
    const apiResponse = await getNonRevenueWaterData(filters);
    
    // Update to match new API response structure
    setAvgResidentialMeter(rounded(apiResponse.avgResidentialMeterConsumption) || 0);
    setAvgResidentialMechanicalMeter(rounded(apiResponse.avgResidentialMechanicalMeterConsumption) || 0);
    setAvgBusinessMeter(rounded(apiResponse.avgBusinessMeterConsumption) || 0);
    setAvgBulkUsage(rounded(apiResponse.avgBulkUsage) || 0);
    setTotalBusinessMeter(apiResponse.totalBusinessMeter || 0);
    setTotalResidentialMeter(apiResponse.totalResidentialMeter || 0);
    setTotalResidentialMechanicalMeter(apiResponse.totalResidentialMechanicalMeter || 0);

    // Guard against trailing points past the requested endDate (e.g. a midnight
    // boundary point). Days are compared in UTC to stay timezone-independent.
    const endDay = filters?.endDate;
    const lineData = (apiResponse.dataLineChartNRW || []).filter((d) => {
      if (!d || !d.date_time) return false;
      // Drop points that have no bulk usage yet (0/null) so the line doesn't
      // dip to zero for periods where data hasn't arrived.
      if (!d.bulk_usage) return false;
      if (!endDay) return true;
      const m = moment.utc(d.date_time);
      return m.isValid() ? m.format("YYYY-MM-DD") <= endDay : true;
    });
    setDataLineChartNRW(lineData);
    setNonRevenueWater(rounded(apiResponse.nonRevenueWater) || 0);

    const dataPie = [
      { name: "Residential digital meters", value: rounded1Decimal(apiResponse.avgResidentialMeterConsumption) || 0 },
      { name: "Residential mechanical meters", value: rounded1Decimal(apiResponse.avgResidentialMechanicalMeterConsumption) || 0 },
      { name: "Business meters", value: rounded1Decimal(apiResponse.avgBusinessMeterConsumption) || 0 },
      { name: "Other usage", value: 0 }, // Based on your table items
      { name: "Bulk usage", value: rounded1Decimal(apiResponse.avgBulkUsage) || 0 },
      { name: "Non revenue water", value: rounded1Decimal(apiResponse.nonRevenueWater) || 0 },
    ];
    setDataPieChart(dataPie);

    console.log("NRW data:", {
      apiResponse,
      dataPie,
    });
  };

  const ticksFormatter = (value) => {
    if (!value && value !== 0) return "";
    let m = moment.utc(value);
    if (!m.isValid() && typeof value === "number") {
      m = moment.utc(value * 1000);
    }
    return m.isValid() ? m.format("ddd MMM DD") : String(value);
  };

  const renderNRWTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const m = moment.utc(label);
    const formattedLabel =
      m && m.isValid() ? m.format("YYYY-MM-DD HH:mm:ss") : String(label);
    return (
      <div
        className="custom-line-tooltip"
        style={{
          backgroundColor: "#fff",
          padding: 8,
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          borderRadius: 6,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 6 }}>{formattedLabel}</div>
        {payload.map((p, i) => (
          <div key={i} style={{ color: p.stroke, fontSize: 13 }}>
            {p.name}: {rounded(p.value ?? 0)} kL
          </div>
        ))}
      </div>
    );
  };

  useEffect(() => {
    const filters = filterRef.current?.getFilters();
    if (filters?.startDate)
      filters.startDate = moment(filters.startDate).format("YYYY-MM-DD");
    if (filters?.endDate)
      filters.endDate = moment(filters.endDate).format("YYYY-MM-DD");
    loadData(filters);
  }, []);

  useLayoutEffect(() => {
    import("./style.scss");
  }, []);

  return (
    <div className="the-content">
      <PageHeading title="Non-Revenue Water" />
      <div className="container-fluid content">
        <div className="action-buttons block-center-between">
          <div className="block-center">
            <NonRevenueWaterFilter ref={filterRef} onFiltersApply={loadData} />
          </div>
        </div>

        <div className="row mt-5">
          <div className="col-md-5">
            <h3 className="title heading-h-usage-customer bg-white">
              <div className="header-container">
                <span className="left-text">
                  Water usage during time period
                </span>
              </div>
            </h3>
            <div className="table-responsive">
              <table className="table customer-list">
                <thead>
                  <tr>
                    <th scope="col"></th>
                    <th scope="col" className="text-center">
                      # meters
                    </th>
                    <th scope="col" className="text-center">
                      Average Usage (kL/day)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <NonRevenueItem
                    avgResidentialMeter={avgResidentialMeter}
                    avgResidentialMechanicalMeter={avgResidentialMechanicalMeter}
                    avgBusinessMeter={avgBusinessMeter}
                    avgBulkUsage={avgBulkUsage}
                    totalBusinessMeter={totalBusinessMeter}
                    totalResidentialMeter={totalResidentialMeter}
                    totalResidentialMechanicalMeter={totalResidentialMechanicalMeter}
                    nonRevenueWater={nonRevenueWater}
                  />
                </tbody>
              </table>
            </div>
            {/* end table */}
          </div>

          <div className="col-md-7">
            {/* start line chart 1 */}
            <div className="line-chart">
              <h3 className="title heading-h-usage-customer bg-white">
                <div className="header-container">
                  <span className="left-text">
                    Pie Chart - Non-Revenue Water
                  </span>
                </div>
              </h3>
            </div>

            <div className="p-4 bg-gray">
              <div style={{ width: "100%", height: 265 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={dataPieChart}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ value }) => `${value} kL`}
                    >
                      {dataPieChart.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={getColorForVariable(entry.name)}
                        />
                      ))}
                    </Pie>
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            {/* end line chart 1 */}
          </div>
        </div>

        <div className="row mt-5 mb-5">
          <div className="col-md-12">
            {/* start line chart 1 */}
            <div className="line-chart">
              <h3 className="title heading-h-usage-customer bg-white">
                <div className="header-container">
                  <span className="left-text">
                    Line Chart - Non-Revenue Water trend
                  </span>
                </div>
              </h3>
            </div>

            <div className="line-chart-container bg-gray">
              <div style={{ width: "100%", height: 300 }}>
                <ResponsiveContainer>
                  <LineChart
                    data={dataLineChartNRW}
                    margin={{ top: 10, right: 20, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid stroke="#ccc" strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date_time"
                      ticks={xTicks}
                      tick={{ fontSize: 13 }}
                      stroke="#e9ebf1"
                      tickFormatter={ticksFormatter}
                    />
                    <YAxis
                      tick={{ fontSize: 13 }}
                      label={{
                        value: "Usage (kL)",
                        angle: -90,
                        position: "insideLeft",
                      }}
                    />
                    {/* <Tooltip /> */}
                    <Tooltip content={renderNRWTooltip} />
                    <Legend verticalAlign="top" height={36} />
                    <Line
                      type="monotone"
                      dataKey="residential_usage"
                      name="Residential usage"
                      stroke={getColorForVariable("Residential usage")}
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="bulk_usage"
                      name="Bulk usage"
                      stroke={getColorForVariable("Bulk usage")}
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
                <h4 style={{ textAlign: "center", marginTop: 4 }}>NRW chart</h4>
              </div>
            </div>
            {/* end line chart 1 */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NonRevenueWaterPage;
